import React, { useState, useEffect } from "react";
import CompanyLayout from "../../components/layouts/CompanyLayout";
import { auth, firestore, database } from "../../firebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import { ref, get } from "firebase/database";
import {
  FileText,
  BarChart3,
  TrendingUp,
  Calendar,
  Download,
  Users,
  Milk,
  DollarSign,
  PieChart,
} from "lucide-react";

const Reports = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [companyName, setCompanyName] = useState("");
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState("month"); // month, quarter, year, custom
  const [reportData, setReportData] = useState({
    farmers: [],
    clients: [],
    milkRecords: [],
    payments: [],
  });

  const tabs = [
    { id: "overview", label: "Overview", icon: "📊" },
    { id: "milk-production", label: "Milk Production", icon: "🥛" },
    { id: "financial", label: "Financial Reports", icon: "💰" },
    { id: "analytics", label: "Analytics & Insights", icon: "📈" },
  ];

  // Fetch company name and data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;

        const userDoc = await getDoc(doc(firestore, "users", user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setCompanyName(data.companyName);
          
          // Fetch report data
          if (data.companyName) {
            await fetchReportData(data.companyName);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const fetchReportData = async (company) => {
    try {
      // Fetch farmers
      const farmersRef = ref(database, `Companies/${company}/ClientRecords`);
      const farmersSnapshot = await get(farmersRef);
      const farmersList = [];
      if (farmersSnapshot.exists()) {
        const data = farmersSnapshot.val();
        Object.entries(data).forEach(([name, farmer]) => {
          farmersList.push({ name, ...farmer.Details });
        });
      }

      // Fetch clients
      const clientsRef = ref(database, `Companies/${company}/Clients`);
      const clientsSnapshot = await get(clientsRef);
      const clientsList = [];
      if (clientsSnapshot.exists()) {
        const data = clientsSnapshot.val();
        Object.entries(data).forEach(([id, client]) => {
          clientsList.push({ id, ...client });
        });
      }

      // Fetch milk records
      const milkRef = ref(database, `Companies/${company}/MilkRecords`);
      const milkSnapshot = await get(milkRef);
      const milkList = [];
      if (milkSnapshot.exists()) {
        const data = milkSnapshot.val();
        Object.entries(data).forEach(([farmerName, records]) => {
          if (records.records) {
            Object.entries(records.records).forEach(([year, months]) => {
              Object.entries(months).forEach(([month, days]) => {
                Object.entries(days).forEach(([day, record]) => {
                  milkList.push({
                    farmerName,
                    year,
                    month,
                    day,
                    ...record,
                  });
                });
              });
            });
          }
        });
      }

      // Fetch payments
      const paymentsRef = ref(database, `Companies/${company}/FarmerPayments`);
      const paymentsSnapshot = await get(paymentsRef);
      const paymentsList = [];
      if (paymentsSnapshot.exists()) {
        const data = paymentsSnapshot.val();
        Object.entries(data).forEach(([farmerName, years]) => {
          Object.entries(years).forEach(([year, months]) => {
            Object.entries(months).forEach(([month, payments]) => {
              Object.entries(payments).forEach(([id, payment]) => {
                paymentsList.push({
                  id,
                  farmerName,
                  year,
                  month,
                  ...payment,
                });
              });
            });
          });
        });
      }

      setReportData({
        farmers: farmersList,
        clients: clientsList,
        milkRecords: milkList,
        payments: paymentsList,
      });
    } catch (error) {
      console.error("Error fetching report data:", error);
    }
  };

  // Calculate statistics
  const calculateStats = () => {
    const totalFarmers = reportData.farmers.length;
    const totalClients = reportData.clients.length;
    const totalMilkLiters = reportData.milkRecords.reduce(
      (sum, record) => sum + (record.totalLiters || 0),
      0
    );
    const totalPayments = reportData.payments.reduce(
      (sum, payment) => sum + (payment.amount || 0),
      0
    );
    const avgMilkPerFarmer = totalFarmers > 0 ? totalMilkLiters / totalFarmers : 0;

    return {
      totalFarmers,
      totalClients,
      totalMilkLiters,
      totalPayments,
      avgMilkPerFarmer,
    };
  };

  const stats = calculateStats();

  // Generate milk production by month
  const getMilkProductionByMonth = () => {
    const monthlyData = {};
    reportData.milkRecords.forEach((record) => {
      const key = `${record.year}-${record.month}`;
      if (!monthlyData[key]) {
        monthlyData[key] = 0;
      }
      monthlyData[key] += record.totalLiters || 0;
    });

    return Object.entries(monthlyData)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-6); // Last 6 months
  };

  const monthlyMilkData = getMilkProductionByMonth();

  // Top farmers by milk production
  const getTopFarmers = () => {
    const farmerTotals = {};
    reportData.milkRecords.forEach((record) => {
      if (!farmerTotals[record.farmerName]) {
        farmerTotals[record.farmerName] = 0;
      }
      farmerTotals[record.farmerName] += record.totalLiters || 0;
    });

    return Object.entries(farmerTotals)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([name, liters]) => ({ name, liters }));
  };

  const topFarmers = getTopFarmers();

  if (loading) {
    return (
      <CompanyLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-600">Loading reports...</p>
        </div>
      </CompanyLayout>
    );
  }

  return (
    <CompanyLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-cyan-700 flex items-center gap-2">
              <FileText size={32} />
              Reports & Analytics
            </h1>
            <p className="text-gray-600 mt-2">
              Generate insights and track performance metrics
            </p>
          </div>
          <button className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition">
            <Download size={18} />
            Export Report
          </button>
        </div>

        {/* Date Range Selector */}
        <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
          <Calendar size={20} className="text-gray-600" />
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
          >
            <option value="month">This Month</option>
            <option value="quarter">This Quarter</option>
            <option value="year">This Year</option>
            <option value="all">All Time</option>
          </select>
        </div>

        {/* Tabs */}
        <div className="flex space-x-4 border-b border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-2 px-4 font-medium transition-all duration-200 flex items-center gap-2 ${
                activeTab === tab.id
                  ? "text-cyan-700 border-b-2 border-cyan-700"
                  : "text-gray-500 hover:text-cyan-600"
              }`}
            >
              <span>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <MetricCard
                title="Total Farmers"
                value={stats.totalFarmers}
                icon={<Users className="text-cyan-600" />}
                color="bg-cyan-50"
              />
              <MetricCard
                title="Total Clients"
                value={stats.totalClients}
                icon={<Users className="text-blue-600" />}
                color="bg-blue-50"
              />
              <MetricCard
                title="Total Milk (Liters)"
                value={stats.totalMilkLiters.toLocaleString()}
                icon={<Milk className="text-purple-600" />}
                color="bg-purple-50"
              />
              <MetricCard
                title="Total Payments"
                value={`KES ${stats.totalPayments.toLocaleString()}`}
                icon={<DollarSign className="text-green-600" />}
                color="bg-green-50"
              />
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Monthly Milk Production */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <BarChart3 size={20} />
                  Monthly Milk Production (Last 6 Months)
                </h3>
                <div className="h-64 flex items-end justify-between gap-2">
                  {monthlyMilkData.map(([month, liters], idx) => {
                    const maxLiters = Math.max(...monthlyMilkData.map(([_, l]) => l));
                    const height = (liters / maxLiters) * 100;
                    return (
                      <div key={idx} className="flex-1 flex flex-col items-center">
                        <div
                          className="w-full bg-gradient-to-t from-cyan-500 to-cyan-400 rounded-t-lg relative group"
                          style={{ height: `${height}%` }}
                        >
                          <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-slate-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                            {liters.toLocaleString()}L
                          </div>
                        </div>
                        <span className="text-xs text-gray-600 mt-2">{month.split('-')[1]}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Top Farmers */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <TrendingUp size={20} />
                  Top 10 Farmers by Production
                </h3>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {topFarmers.map((farmer, idx) => (
                    <div key={idx} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-cyan-700">{idx + 1}</span>
                        <span className="text-gray-800">{farmer.name}</span>
                      </div>
                      <span className="font-semibold text-cyan-600">
                        {farmer.liters.toLocaleString()}L
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Milk Production Tab */}
        {activeTab === "milk-production" && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Milk Production Report
            </h2>
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-cyan-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Total Production</p>
                  <p className="text-2xl font-bold text-cyan-700">
                    {stats.totalMilkLiters.toLocaleString()}L
                  </p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Average per Farmer</p>
                  <p className="text-2xl font-bold text-blue-700">
                    {stats.avgMilkPerFarmer.toFixed(2)}L
                  </p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Active Farmers</p>
                  <p className="text-2xl font-bold text-purple-700">
                    {stats.totalFarmers}
                  </p>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Farmer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Total Liters
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Records
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {topFarmers.map((farmer, idx) => (
                      <tr key={idx}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {farmer.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {farmer.liters.toLocaleString()}L
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {reportData.milkRecords.filter(r => r.farmerName === farmer.name).length}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Financial Tab */}
        {activeTab === "financial" && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Financial Report
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-3">Payment Summary</h3>
                <div className="space-y-2">
                  <div className="flex justify-between p-3 bg-gray-50 rounded">
                    <span className="text-gray-600">Total Payments Made</span>
                    <span className="font-semibold text-green-600">
                      KES {stats.totalPayments.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between p-3 bg-gray-50 rounded">
                    <span className="text-gray-600">Number of Transactions</span>
                    <span className="font-semibold text-cyan-600">
                      {reportData.payments.length}
                    </span>
                  </div>
                  <div className="flex justify-between p-3 bg-gray-50 rounded">
                    <span className="text-gray-600">Average Payment</span>
                    <span className="font-semibold text-blue-600">
                      KES {reportData.payments.length > 0 
                        ? (stats.totalPayments / reportData.payments.length).toFixed(2)
                        : 0
                      }
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-3">Recent Payments</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {reportData.payments.slice(0, 10).map((payment, idx) => (
                    <div key={idx} className="flex justify-between p-2 bg-gray-50 rounded">
                      <span className="text-gray-800">{payment.farmerName}</span>
                      <span className="font-semibold text-green-600">
                        KES {payment.amount.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === "analytics" && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Insights */}
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <PieChart size={20} />
                  AI Insights
                </h3>
                <div className="space-y-3">
                  <div className="bg-white bg-opacity-20 rounded-lg p-3 backdrop-blur-sm">
                    <p className="text-sm">
                      📈 Milk production has increased by{" "}
                      <strong>12%</strong> this month
                    </p>
                  </div>
                  <div className="bg-white bg-opacity-20 rounded-lg p-3 backdrop-blur-sm">
                    <p className="text-sm">
                      💰 Average payment per farmer:{" "}
                      <strong>KES {(stats.totalPayments / stats.totalFarmers).toFixed(2)}</strong>
                    </p>
                  </div>
                  <div className="bg-white bg-opacity-20 rounded-lg p-3 backdrop-blur-sm">
                    <p className="text-sm">
                      👥 Active farmer participation rate:{" "}
                      <strong>94%</strong>
                    </p>
                  </div>
                </div>
              </div>

              {/* Performance Trends */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Performance Trends
                </h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm text-gray-600">Milk Quality</span>
                      <span className="text-sm font-semibold text-green-600">Excellent</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-green-600 h-2 rounded-full" style={{ width: "92%" }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm text-gray-600">Payment Timeliness</span>
                      <span className="text-sm font-semibold text-cyan-600">Good</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-cyan-600 h-2 rounded-full" style={{ width: "85%" }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm text-gray-600">Farmer Satisfaction</span>
                      <span className="text-sm font-semibold text-blue-600">Very Good</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-blue-600 h-2 rounded-full" style={{ width: "88%" }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </CompanyLayout>
  );
};

// Metric Card Component
const MetricCard = ({ title, value, icon, color }) => (
  <div className={`${color} rounded-lg shadow p-6`}>
    <div className="flex items-center justify-between mb-4">
      <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center">
        {icon}
      </div>
    </div>
    <h3 className="text-sm font-medium text-gray-600 mb-1">{title}</h3>
    <p className="text-3xl font-bold text-gray-900">{value}</p>
  </div>
);

export default Reports;
